import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { notifyOk, notifyError } from "@/lib/notify";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SiteForm from "@/components/forms/SiteForm";
import { Plus, MapPin, Link2 } from "lucide-react";

export default function SitiosTab({ oid, orgNombre, orgTipo, sitios, vinculables, onReload }) {
  const navigate = useNavigate();
  const [formOpen, setFormOpen] = useState(false);
  const [legacy, setLegacy] = useState("");

  const vincular = async () => {
    if (!legacy) return;
    try {
      await api.post(`/sitios/${legacy}/organization`, { organization_id: oid });
      notifyOk("Sitio vinculado. El anclaje es definitivo.");
      setLegacy("");
      onReload();
    } catch (e) {
      notifyError(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-5" data-testid="tab-sitios">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <p className="text-xs text-slate-500 max-w-xl">
          El sitio es el lugar operativo. Un sitio nuevo nace anclado a esta organización y el
          anclaje ya no se mueve. No se repiten aquí los datos fiscales.
        </p>
        <Button data-testid="add-site-from-org" onClick={() => setFormOpen(true)}
          className="rounded-xl bg-[#01CBE3] hover:bg-[#01B5CB] text-white shrink-0 w-full sm:w-auto">
          <Plus className="h-4 w-4 mr-1.5" /> Nuevo sitio
        </Button>
      </div>

      {sitios.length === 0 ? (
        <p data-testid="org-sitios-empty" className="text-slate-400 text-sm">Esta organización todavía no tiene sitios.</p>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-100">
          {sitios.map((s) => (
            <button key={s.id} data-testid={`org-sitio-${s.id}`} onClick={() => navigate(`/sitios/${s.id}`)}
              className="w-full text-left px-5 py-4 flex items-center gap-3 hover:bg-slate-50/70">
              <MapPin className="h-5 w-5 text-[#012472] shrink-0" strokeWidth={1.5} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-[#012472] truncate">{s.nombre}</p>
                <p className="text-xs text-slate-500 truncate">
                  {s.ciudad || "—"} · {(s.modalidad || "residencial") === "institucional" ? "Institucional" : "Residencial"}
                </p>
              </div>
              {s.estado === "Inactivo" && <span className="text-[10px] rounded-full bg-slate-100 text-slate-500 px-2 py-0.5">Inactivo</span>}
            </button>
          ))}
        </div>
      )}

      {(vinculables || []).length > 0 && (
        <div className="rounded-xl border border-gray-100 p-4 space-y-3" data-testid="vincular-legacy-block">
          <p className="text-sm font-medium text-[#012472]">Asignar organización a un sitio existente</p>
          <p className="text-xs text-slate-500">Asignación de una sola vez: después, el sitio ya no se mueve de organización.</p>
          <div className="flex flex-wrap gap-2">
            <Select value={legacy} onValueChange={setLegacy}>
              <SelectTrigger className="rounded-xl w-full sm:w-[280px]" data-testid="legacy-site-select">
                <SelectValue placeholder="Elige un sitio" />
              </SelectTrigger>
              <SelectContent>
                {vinculables.map((s) => (<SelectItem key={s.id} value={s.id}>{s.nombre || "(sin nombre)"}</SelectItem>))}
              </SelectContent>
            </Select>
            <Button variant="outline" className="rounded-xl w-full sm:w-auto" disabled={!legacy} onClick={vincular} data-testid="vincular-legacy-button">
              <Link2 className="h-4 w-4 mr-1.5" /> Vincular
            </Button>
          </div>
        </div>
      )}

      <SiteForm open={formOpen} onOpenChange={setFormOpen} site={null}
        organizationId={oid} organizationNombre={orgNombre} organizationTipo={orgTipo} onSaved={onReload} />
    </div>
  );
}
